import { atom, useAtom } from "jotai";
import { generateSimulationEnMasseData } from "./flotteUtils";
import { atomWithReset } from "jotai/utils";
import { useCallback, useRef } from "react";
import tools from "../../../../helpers/tools";
import flotteService from "../../../../services/flotteService";
import { mouvementOptions } from "../components/FlotteSimulator/simulatorUtils";
import { modalActions } from "../../../../context/actions/modalActions";
import useStore from "../../../../context/useStore";
import SimulationEnMasse from "../components/SimulationEnMasse/SimulationEnMasse";
import SecteurEnMasse from "../components/SecteurEnMasse/SecteurEnMasse";
import { columnsConfig } from "../components/FlotteTable/flotteColumns.config";
import { toast } from "react-toastify";
import { isVORole } from "../components/FlotteTable/FlotteUtils";

export const pageSize = 30;
/**
 * Transforme l'array mouvements en attributs séparés pour chaque type de mouvement
 * @param {Object} item - L'item de flotte avec potentiellement un array "mouvements"
 * @returns {Object} - L'item avec les nouveaux attributs mouvement_*
 */
export const transformMovements = (item) => {
  // Vérification de sécurité : s'assurer que projections existe et est un array
  if (!item.projections || !Array.isArray(item.projections)) {
    console.warn(
      "transformMovements: item.projections est manquant ou invalide",
      item,
    );
    return {
      ...item,
      mouvements: [],
    };
  }

  // Tri stable : on conserve l'ordre renvoyé par l'API pour les mouvements
  // officiels, et on déplace les simulations en fin de tableau.
  // Conséquence importante : dans transformedItem.mouvement_<type>, le DERNIER
  // mouvement officiel d'un type donné (ex: fin_reelle) écrase les précédents
  // (cf. forEach plus bas) — l'API doit donc renvoyer les mouvements dans
  // l'ordre voulu pour cet écrasement.
  const isSimulation = (m) =>
    m.type === "simulation" || m.type === "simulation_prorata_km";
  const itemWithSortedMovements = {
    ...item,
    mouvements: [...item.projections].sort((a, b) => {
      if (isSimulation(a) && !isSimulation(b)) return 1;
      if (!isSimulation(a) && isSimulation(b)) return -1;
      return 0;
    }),
  };
  // Copier l'item original
  const transformedItem = {
    ...itemWithSortedMovements,
  };

  // Définir les types de mouvements attendus et les champs à extraire.
  // On exclut "simulation" libre, mais on garde "simulation_prorata_km" car
  // certaines colonnes du tableau (ex: dépassement kilométrique) en exposent
  // les données via mouvement_simulation_prorata_km.
  const movementTypes = mouvementOptions
    .map((option) => option.id)
    .filter((id) => id !== "simulation");

  // Mapping des noms de champs API vers les noms attendus dans columnsConfig
  // Ce mapping est nécessaire car l'API et le tableau utilisent des noms différents
  const fieldMapping = {
    date: "date_retour", // API: 'date' -> Tableau: 'date_retour'
    kilometrage: "km", // API: 'kilometrage' -> Tableau: 'km'
    montant_kilometres_supplementaires: "km_sup", // API: 'montant_kilometres_supplementaires' -> Tableau: 'km_sup'
    frais_remise_en_etat_devis: "frais_remise_en_etat", // API: 'frais_remise_en_etat_devis' -> Tableau: 'frais_remise_en_etat'
    // Les champs suivants ont les mêmes noms dans l'API et columnsConfig :
    // duree, taux_vr, montant_vr, valeur_theorique, indemnites,
    // frais_remise_en_etat_facture, cout_remise_en_etat_facture
  };

  // Fonction helper pour extraire les données d'un mouvement avec mapping des noms
  const extractMovementData = (mouvement = {}) => {
    const data = {};

    // Parcourir tous les champs du mouvement API
    Object.keys(mouvement).forEach((apiField) => {
      // Déterminer le nom du champ dans le tableau (mappé ou identique)
      const tableFieldName = fieldMapping[apiField] || apiField;

      // Stocker la valeur sous le nom attendu par le tableau
      data[tableFieldName] = mouvement[apiField];
    });

    // Pour le mouvement "contrat", utiliser montant_vr comme valeur_theorique
    if (
      (mouvement.type === "contrat" || mouvement.mouvement === "contrat") &&
      mouvement.montant_vr !== undefined
    ) {
      data.valeur_theorique = mouvement.montant_vr;
    }

    return data;
  };

  // Initialiser tous les types de mouvements avec des valeurs par défaut
  movementTypes.forEach((type) => {
    transformedItem[`mouvement_${type}`] = extractMovementData();
  });

  // Si l'item a un array mouvements, le traiter
  if (Array.isArray(itemWithSortedMovements.mouvements)) {
    itemWithSortedMovements.mouvements.forEach((mouvement) => {
      const type = mouvement.mouvement || mouvement.type;
      if (movementTypes.includes(type)) {
        transformedItem[`mouvement_${type}`] = extractMovementData(mouvement);
      }
    });
  }

  // ajouter la durée et km du dernier mouvement
  const officialMouvements = itemWithSortedMovements.mouvements.filter(
    (m) => m.type !== "simulation" && m.type !== "simulation_prorata_km",
  );
  const lastMovement = officialMouvements[officialMouvements.length - 1];
  if (lastMovement) {
    transformedItem.duree_reelle = lastMovement.duree;
  }
  if (transformedItem.mesures?.length > 0) {
    const lastMesure = transformedItem.mesures[transformedItem.mesures.length - 1];
    transformedItem.km_reel = lastMesure.kilometrage;
    transformedItem.date_dernier_km = lastMesure.date ?? null;
  } else {
    const finReelleMovements = officialMouvements.filter(
      (m) => (m.mouvement || m.type) === "fin_reelle",
    );
    const lastFinReelle = finReelleMovements[finReelleMovements.length - 1];
    if (
      lastFinReelle?.kilometrage !== undefined &&
      lastFinReelle?.kilometrage !== null
    ) {
      transformedItem.km_reel = lastFinReelle.kilometrage;
      transformedItem.date_dernier_km = lastFinReelle.date ?? null;
    }
  }

  // Dernière position et date (la position peut être réinitialisée → null)
  const positionHist = transformedItem.position_historique || [];
  if (positionHist.length > 0) {
    const lastPosition = positionHist[positionHist.length - 1];
    transformedItem.date_derniere_position = lastPosition.date || null;
    transformedItem.derniere_position = transformedItem.position ?? null;
  }

  // Dernière localisation et date
  const localisationHist = transformedItem.localisation_historique || [];
  if (localisationHist.length > 0) {
    const lastLocalisation = localisationHist[localisationHist.length - 1];
    transformedItem.date_derniere_localisation = lastLocalisation.date || null;
    transformedItem.derniere_localisation =
      transformedItem.localisation ?? null;
  }

  // Dernier statut et date
  const statutHist = transformedItem.statut_historique || [];
  if (statutHist.length > 0) {
    const lastStatut = statutHist[statutHist.length - 1];
    transformedItem.date_dernier_statut = lastStatut.date || null;
  }

  return transformedItem;
};

export const flotteListAtom = atom([]);
export const flotteMetaAtom = atom({});
export const flotteFiltersAtom = atomWithReset({});
export const flottePageAtom = atom(1);
export const selectedRowsAtom = atom(new Set());
export const flotteHasMoreAtom = atom(true);
const isLoadingAtom = atom(false);

const useFlotte = () => {
  const [flotteList, setFlotteList] = useAtom(flotteListAtom);
  const [, setFlotteMeta] = useAtom(flotteMetaAtom);
  const [flotteFilters] = useAtom(flotteFiltersAtom);
  const [flottePage, setFlottePage] = useAtom(flottePageAtom);
  const [selectedRows, setSelectedRows] = useAtom(selectedRowsAtom);
  const [isLoading, setIsLoading] = useAtom(isLoadingAtom);
  const [hasMore, setHasMore] = useAtom(flotteHasMoreAtom);
  const [state, dispatch] = useStore();
  const modal_actions = modalActions(state, dispatch);

  // Ref pour tracker le chargement en cours sans dépendre du cycle de render
  const isLoadingRef = useRef(false);
  // Ref pour annuler les requêtes obsolètes (chaque appel incrémente le compteur)
  const requestIdRef = useRef(0);

  const loadFlotteList = useCallback(
    async (filters, page, append = false) => {
      // Marquer la requête en cours et capturer son ID
      const currentRequestId = ++requestIdRef.current;
      isLoadingRef.current = true;
      setIsLoading(true);

      try {
        let response = null;
        if (isVORole(state.auth?.user?.role)) {
          response = await flotteService.getFlotteListVO({
            ...filters,
            page,
            pageSize,
          });
        } else {
          response = await flotteService.getFlotteList({
            ...filters,
            page,
            pageSize,
          });
        }

        // Si une requête plus récente a été lancée, ignorer ce résultat
        if (currentRequestId !== requestIdRef.current) return [];

        setFlotteMeta(response.meta);

        const rawItems = Array.isArray(response?.data) ? response.data : [];
        // Transformer les mouvements en attributs séparés
        const newItems = rawItems.map((data) =>
          transformMovements({
            ...data,
          }),
        );
        const itemsReceived = newItems.length;

        // Calculer hasMore en utilisant les métadonnées de pagination
        let moreAvailable = false;
        if (response?.meta) {
          const totalItems =
            response.meta.total || response.meta.totalItems || 0;
          // Pour append, on va calculer le total dans le setter de setFlotteList
          moreAvailable = totalItems > 0 && page * pageSize < totalItems;
          console.log(
            `Page ${page}: ${itemsReceived} éléments reçus, total disponible: ${totalItems}, hasMore: ${moreAvailable}`,
          );
        } else {
          // Fallback sur l'ancienne méthode si pas de métadonnées
          moreAvailable = itemsReceived >= pageSize;
        }
        setHasMore(moreAvailable);

        // Mettre à jour la liste des véhicules
        if (append && page > 1) {
          setFlotteList((prev) => {
            // Éviter les doublons
            const existingIds = new Set(prev.map((item) => item.id));
            const uniqueNewItems = newItems.filter(
              (item) => !existingIds.has(item.id),
            );
            return [...prev, ...uniqueNewItems];
          });
        } else {
          setFlotteList(newItems);
        }
        console.log("newItems", newItems);
        return newItems;
      } catch (error) {
        // Ignorer les erreurs de requêtes obsolètes
        if (currentRequestId !== requestIdRef.current) return [];
        console.error("Erreur lors du chargement de la flotte:", error);
        tools.filterError(() => {}, error?.response?.data, "");
        return [];
      } finally {
        // Ne mettre à jour isLoading que si c'est la requête la plus récente
        if (currentRequestId === requestIdRef.current) {
          isLoadingRef.current = false;
          setIsLoading(false);
        }
      }
    },
    [setFlotteList],
  );

  const onLoadFlotteList = useCallback(
    async (filters = {}, page = 1) => {
      setFlottePage(page);
      setHasMore(true);
      return await loadFlotteList(filters, page, false);
    },
    [loadFlotteList, setFlottePage],
  );

  const onRefreshFlotteList = useCallback(async () => {
    await onLoadFlotteList(flotteFilters, flottePage, false);
  }, [onLoadFlotteList]);

  const onLoadNextPage = useCallback(async () => {
    if (isLoading || !hasMore) return;

    const nextPage = flottePage + 1;
    const newItems = await loadFlotteList(flotteFilters, nextPage, true);

    if (newItems && newItems.length > 0) {
      setFlottePage(nextPage);
    } else {
      setHasMore(false);
    }
  }, [
    flotteFilters,
    flottePage,
    isLoading,
    hasMore,
    loadFlotteList,
    setFlottePage,
  ]);

  // Met à jour une projection dans la liste globale après modification dans le simulateur,
  // pour que la modale de détail conserve les changements quand elle est rouverte.
  const onUpdateProjection = useCallback(
    (itemId, projectionIndex, updatedProjection) => {
      setFlotteList((prev) =>
        prev.map((oldItem) => {
          if (oldItem.id !== itemId) return oldItem;
          const newMouvements = [...(oldItem.mouvements || [])];
          if (newMouvements[projectionIndex]) {
            newMouvements[projectionIndex] = {
              ...newMouvements[projectionIndex],
              ...updatedProjection,
            };
          }
          const updatedItem = { ...oldItem, mouvements: newMouvements };
          // Réappliquer transformMovements pour recalculer mouvement_*, duree_reelle, km_reel...
          return transformMovements({
            ...updatedItem,
            projections: newMouvements,
          });
        }),
      );
    },
    [setFlotteList],
  );

  /**
   * LS-742 : relit les causes de l'anomalie de facturation du véhicule après une saisie VO ou
   * un déverrouillage, pour que le marqueur rouge reflète la correction sans recharger la liste.
   * Meilleur effort : un échec laisse la ligne telle quelle.
   */
  const rafraichirAnomaliesFacturation = async (item) => {
    try {
      const anomalies_facturation = await flotteService.getAnomaliesFacturation({
        item,
      });
      setFlotteList((prev) =>
        prev.map((oldItem) =>
          oldItem.id === item.id ? { ...oldItem, anomalies_facturation } : oldItem,
        ),
      );
    } catch (error) {
      console.error(
        "Erreur lors du rafraîchissement des causes d'anomalie de facturation:",
        error,
      );
    }
  };

  const onUpdateCell = async (item, updatedCell, value) => {
    console.log(item, updatedCell, value);
    // on doit récupérer le bon nom d'attribut dans columnsConfig
    const cellName = columnsConfig[updatedCell].path.split(".")[1];
    const newProjection = {
      ...item.occasion,
      [cellName]: value,
      champ_saisi: updatedCell,
    };
    try {
      const updatedItem = await flotteService.updateCell({
        projection: newProjection,
        item,
        modifiedField: cellName,
      });
      console.log("updatedItem", updatedItem);
      setFlotteList((prev) =>
        prev.map((oldItem) => {
          if (oldItem.id === item.id) {
            return {
              ...oldItem,
              occasion: { ...oldItem.occasion, ...updatedItem },
            };
          }
          return oldItem;
        }),
      );
      await rafraichirAnomaliesFacturation(item);
    } catch (error) {
      // LS-738 : l'API refuse (409) la saisie d'une donnée figée par l'import de facturation
      // avec un message qui nomme l'import ; on le montre tel quel.
      toast.error(
        error?.response?.data?.message ||
          "Erreur lors de la mise à jour de la cellule",
      );
      console.error("Erreur lors de la mise à jour de la cellule:", error);
    }
  };

  /**
   * LS-738 : lève le verrou de facturation d'un véhicule (super admin). Date de facture,
   * client facturé et prix facturé redeviennent modifiables jusqu'au prochain import sans
   * anomalie, qui refige le véhicule.
   */
  const onDeverrouillerFacturation = async (item) => {
    const immatriculation = item?.identification?.immatriculation || "ce véhicule";
    const confirme = window.confirm(
      `Déverrouiller la facturation de ${immatriculation} ?\n\nLa date de facture, le client facturé et le prix facturé redeviendront modifiables jusqu'au prochain import de facturation sans anomalie.`,
    );
    if (!confirme) return;

    try {
      const occasion = await flotteService.deverrouillerFacturation({ item });
      setFlotteList((prev) =>
        prev.map((oldItem) =>
          oldItem.id === item.id
            ? { ...oldItem, occasion: { ...oldItem.occasion, ...occasion } }
            : oldItem,
        ),
      );
      toast.success(`Facturation de ${immatriculation} déverrouillée`);
      await rafraichirAnomaliesFacturation(item);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Erreur lors du déverrouillage",
      );
      console.error("Erreur lors du déverrouillage de la facturation:", error);
    }
  };

  // Réinitialiser la pagination quand les filtres changent
  const onFiltersChange = useCallback(async () => {
    await onLoadFlotteList(flotteFilters);
  }, [flotteFilters, onLoadFlotteList]);

  // Fonctions de gestion de la sélection
  const toggleRowSelection = useCallback(
    (itemId) => {
      setSelectedRows((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(itemId)) {
          newSet.delete(itemId);
        } else {
          newSet.add(itemId);
        }
        return newSet;
      });
    },
    [setSelectedRows],
  );

  const selectAllRows = useCallback(() => {
    const allIds = new Set(flotteList.map((item) => item.id));
    setSelectedRows(allIds);
  }, [flotteList, setSelectedRows]);

  const deselectAllRows = useCallback(() => {
    setSelectedRows(new Set());
  }, [setSelectedRows]);

  const toggleAllRowsSelection = useCallback(() => {
    if (selectedRows.size === flotteList.length && flotteList.length > 0) {
      deselectAllRows();
    } else {
      selectAllRows();
    }
  }, [selectedRows.size, flotteList.length, deselectAllRows, selectAllRows]);

  const isRowSelected = useCallback(
    (itemId) => {
      return selectedRows.has(itemId);
    },
    [selectedRows],
  );

  const isAllRowsSelected = useCallback(() => {
    return flotteList.length > 0 && selectedRows.size === flotteList.length;
  }, [flotteList.length, selectedRows.size]);

  const isSomeRowsSelected = useCallback(() => {
    return selectedRows.size > 0 && selectedRows.size < flotteList.length;
  }, [selectedRows.size, flotteList.length]);

  const simulerEnMasse = useCallback(() => {
    console.log(
      "Simulation en masse pour",
      selectedRows.size,
      "véhicules sélectionnés",
    );
    console.log("IDs sélectionnés:", Array.from(selectedRows));

    // Génération des données de simulation en masse
    const simulationData = generateSimulationEnMasseData(
      flotteList,
      selectedRows,
    );
    console.log("Données de simulation générées:", simulationData);

    // TODO: Traiter les données de simulation (appel API, etc.)
    if (simulationData.length > 0) {
      console.log(
        `${simulationData.length} véhicules prêts pour la simulation en masse`,
      );
      simulationData.forEach((data, index) => {
        console.log(`Véhicule ${index + 1}:`, {
          id: data.vehicleId,
          info: `${data.vehicleInfo.marque} ${data.vehicleInfo.modele} (${data.vehicleInfo.immatriculation})`,
          client: data.vehicleInfo.client,
          dernierMouvement: `${data.lastMovement.type} du ${new Date(
            data.lastMovement.date,
          ).toLocaleDateString()}`,
        });
      });
      modal_actions.updateModal({
        isOpen: true,
        content: (
          <SimulationEnMasse
            data={simulationData}
            onComplete={onRefreshFlotteList}
          />
        ),
      });
    } else {
      console.warn("Aucune donnée de simulation générée");
    }
  }, [selectedRows, flotteList, onRefreshFlotteList]);

  const modifierSecteurEnMasse = useCallback(() => {
    const vehiculesSelectionnes = flotteList
      .filter((item) => selectedRows.has(item.id))
      .map((item) => ({
        vehicleId: item.id,
        vehicleInfo: {
          marque: item.identification?.marque,
          modele: item.identification?.modele,
          immatriculation: item.identification?.immatriculation,
          client: item.contrat?.intitule_client,
        },
      }));

    if (vehiculesSelectionnes.length === 0) {
      console.warn(
        "Aucun véhicule sélectionné pour la modification de secteur en masse",
      );
      return;
    }

    modal_actions.updateModal({
      isOpen: true,
      content: (
        <SecteurEnMasse
          data={vehiculesSelectionnes}
          onComplete={onRefreshFlotteList}
        />
      ),
    });
  }, [selectedRows, flotteList, onRefreshFlotteList]);

  return {
    actions: {
      onRefreshFlotteList,
      onLoadFlotteList,
      onLoadNextPage,
      onFiltersChange,
      loadFlotteList,
      onUpdateCell,
      onDeverrouillerFacturation,
      onUpdateProjection,
      toggleRowSelection,
      selectAllRows,
      deselectAllRows,
      toggleAllRowsSelection,
      simulerEnMasse,
      modifierSecteurEnMasse,
    },
    state: {
      isLoading,
      hasMore,
      selectedRows,
      isRowSelected,
      isAllRowsSelected,
      isSomeRowsSelected,
    },
  };
};

export default useFlotte;
